import { Divider, Modal, Button, Input, message } from "antd";
import { Procedure } from "../../types/procedures";
import { procedureDisplayName } from "../../utils/procedureDisplayName";
import { useQuote, PromoCodePayload } from "./QuoteProvider";
import "./css/quote-summary.css";
import {
  formatCurrency,
  formatCappedCurrency as capCurrency,
  DEFAULT_QUOTE_DISPLAY_CAP,
} from "../../utils/currencyFormatter";
import { estimateMonthlyPayment } from "../../utils/financingEstimate";
import { withFinancingUtm } from "../../utils/financingApplyUrl";
import {
  CloseCircleOutlined,
  CheckCircleOutlined,
  PictureOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useEffect, useState, useRef } from "react";
import { useAppConfig } from "../hooks/useAppConfig";
import { useFeatureFlags } from "../providers/FeatureFlagsProvider";
import BookNowButton, {
  BookNowButtonHandle,
} from "./components/BookNowButton/BookNowButton";
import QuoteButton from "./components/QuoteButton/QuoteButton";
import TrustBand from "./components/TrustBand/TrustBand";
import PriceCapPanel from "./components/PriceCapPanel/PriceCapPanel";
import { useIsMobile } from "./MobileBreakpointContext";
import {
  trackFinancingForwardExposure,
  trackFinancingApplyClick,
  trackQuotePhotoUploadStarted,
  trackQuotePhotoUploadCompleted,
  trackQuotePhotoUploadFailed,
  classifyUploadResponse,
  classifyUploadNetworkError,
} from "../../utils/quoteAnalytics";
import { readUploadedFileKey } from "../../utils/uploadResponse";

// @ts-ignore
import { useTranslation } from "react-i18next";

export const QuoteSummaryTreatment = ({
  showPricing = false,
  showBookingCTA = false,
  showFileUploads = false,
}: {
  showPricing?: boolean;
  showBookingCTA?: boolean;
  showFileUploads?: boolean;
}) => {
  // @ts-ignore
  const { t } = useTranslation("quote");
  // @ts-ignore
  const { t: tProcedures, i18n } = useTranslation("procedures");

  const {
    quoteId,
    quoteStatus,
    name,
    selectedProcedures,
    selectedProcedureTypes,
    selectedSurgeon,
    removeSelectedProcedure,
    setSelectedProcedureTypes,
    getQuotePricing,
    getPatientDisplayRows,
    getSelectedProcedureTypePriceRanges,
    fileList,
    setFileList,
    saveFilesToQuote,
    markFinancingApplyClicked,
    markEvaluationRequested,
    appliedPromoCode,
    setAppliedPromoCode,
    giftPass,
  } = useQuote();

  const appConfig = useAppConfig();

  const { isEnabled } = useFeatureFlags();
  const showPrices = isEnabled("enablePricing") && showPricing;

  // photo-gated-price-v1 A/B CONCLUDED 2026-08-25 — "photo_gate" won and the
  // ESTIMATE-ONLY presentation now serves 100% of traffic, so there is no
  // enrollment call and no arm to read here: the gated presentation is
  // UNCONDITIONAL. Every former `gatedPresentation ? gated : control` branch in
  // this file has been collapsed to the gated side and the control-only
  // rendering deleted. See abTestConfigs.ts retired block + the
  // ABTestResolution server overlay.
  const selectedProcedureTypeRanges = getSelectedProcedureTypePriceRanges();
  const hasSimplifiedProcedureTypes = selectedProcedureTypeRanges.length > 0;

  const isMobile = useIsMobile();
  // financing-forward-v1 A/B CONCLUDED — the treatment (monthly-forward hero +
  // prequalify CTA) won and now serves 100% of priced traffic, so we no longer
  // call useABTestVariant. The monthly hero renders wherever prices show; the
  // old price-forward "Total" layout below survives ONLY as the zero-price
  // fallback (showFinancingTreatment === false ⇔ monthlyPayment <= 0). See
  // abTestConfigs.ts (status:"finished") + the ABTestResolution server overlay.
  const financingForward = showPrices;

  const [showPromoCodeInput, setShowPromoCodeInput] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [promoCodeError, setPromoCodeError] = useState("");
  const [isApplyingPromoCode, setIsApplyingPromoCode] = useState(false);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  // Batch upload progress for the promo card's in-flight state (owner
  // 2026-08-14: photo 1-of-N landing must not flip the card to the success
  // state while N-1 are still uploading). activeUploadBatchesRef counts
  // concurrent handlePhotoUpload calls so overlapping batches (e.g. a second
  // tap while the first is still uploading) can't have one batch's `finally`
  // clear the spinner out from under the other.
  const [uploadProgress, setUploadProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const activeUploadBatchesRef = useRef(0);
  const photoFileInputRef = useRef<HTMLInputElement>(null);

  // When the inline Stripe payment panel is open (driven up from BookNowButton),
  // collapse the procedures/price summary into a deposit-focused view so the
  // card form doesn't read as "paying the full procedure price". bookingDepositCents
  // is the authoritative amount being charged (matches the panel's "Pay $X").
  const [bookingPaymentMode, setBookingPaymentMode] = useState(false);
  const [bookingDepositCents, setBookingDepositCents] = useState<number | null>(
    null,
  );
  const bookingPanelRef = useRef<HTMLDivElement>(null);

  // Entering payment mode collapses the content ABOVE the panel (procedures,
  // price, photo card). On mobile (funnel is mobile-first) that can leave the
  // card form scrolled off the top of the viewport, so scroll it into view.
  useEffect(() => {
    if (bookingPaymentMode && bookingPanelRef.current) {
      bookingPanelRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [bookingPaymentMode]);

  const [evalModalOpen, setEvalModalOpen] = useState(false);
  const bookNowRef = useRef<BookNowButtonHandle>(null);
  // Set when the patient taps the modal's "Book now". The booking is fired in
  // the modal's afterClose so the AntD modal fully unmounts BEFORE the inline
  // payment panel opens (no modal-over-panel stacking).
  const triggerBookingOnCloseRef = useRef(false);

  // Ghost-tap guard: if the user just tapped "Next" on the previous step and
  // the promo-code button mounted under their finger, the buffered click can
  // open the promo input. Block clicks for 500ms after mount.
  const [promoCodeReady, setPromoCodeReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setPromoCodeReady(true), 500);
    return () => clearTimeout(t);
  }, []);
  const handleShowPromoCode = () => {
    if (!promoCodeReady) return;
    setShowPromoCodeInput(true);
  };

  const handlePhotoUpload = async (files: File[]) => {
    const fileCount = files.length;
    const batchStartedAt = Date.now();
    trackQuotePhotoUploadStarted({ fileCount });
    // Analytics-only — set the first time this batch sees a bad outcome,
    // never overwritten after.
    let batchErrorBucket: string | null = null;
    // A rejected/malformed upload must never become a fileList entry (a real
    // patient's estimate was silently blocked 4 days by exactly that — see
    // uploadResponse.ts). failedCount drives the batch-end error message and
    // is independent of batchErrorBucket, which only feeds analytics.
    let failedCount = 0;

    activeUploadBatchesRef.current += 1;
    setIsUploadingPhotos(true);
    setUploadProgress((p) =>
      p ? { done: p.done, total: p.total + files.length } : { done: 0, total: files.length },
    );
    let currentList = [...fileList];
    let completedInBatch = 0;
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append("file", file);
        // Per-file try/catch: one rejected file must not abort the rest of
        // the batch — every remaining file still gets a shot.
        try {
          const response = await fetch(
            process.env.PUBLIC_URL + "/s3/upload-s3-direct",
            { method: "POST", body: formData },
          );
          if (batchErrorBucket === null) {
            const outcome = classifyUploadResponse(response);
            if (!outcome.ok) batchErrorBucket = outcome.errorBucket;
          }
          const fileKey = await readUploadedFileKey(response);
          const uid = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const newFile = { uid, response: { url: fileKey } };
          currentList = [...currentList, newFile];
          setFileList(currentList);
          saveFilesToQuote(currentList);
        } catch (error) {
          console.error("Photo upload error:", error);
          failedCount += 1;
          if (batchErrorBucket === null) {
            batchErrorBucket = classifyUploadNetworkError(error).errorBucket;
          }
        }
        completedInBatch += 1;
        setUploadProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
      }
    } finally {
      activeUploadBatchesRef.current -= 1;
      if (activeUploadBatchesRef.current === 0) {
        setIsUploadingPhotos(false);
        setUploadProgress(null);
      } else {
        // Defensive: every file above is now caught individually, so this
        // batch never aborts early — kept in case a future edit reintroduces
        // an uncaught throw, so "photo X of N" still can't stall short of N.
        const unfinished = files.length - completedInBatch;
        if (unfinished > 0) {
          setUploadProgress((p) =>
            p ? { ...p, total: Math.max(p.done, p.total - unfinished) } : p,
          );
        }
      }
      const durationMs = Date.now() - batchStartedAt;
      if (batchErrorBucket) {
        trackQuotePhotoUploadFailed({
          fileCount,
          durationMs,
          errorBucket: batchErrorBucket,
        });
      } else {
        trackQuotePhotoUploadCompleted({ fileCount, durationMs });
      }
      if (failedCount > 0) {
        message.error(
          failedCount === 1
            ? t("photos.uploadFailed", { count: failedCount })
            : t("photos.uploadFailedPlural", { count: failedCount }),
        );
      }
    }
  };

  const isBooked = quoteStatus === "BOOKED";
  const hasUploadedPhotos = fileList.length > 0;
  // Gated presentation: every dollar figure on this surface is suppressed and
  // replaced by the no-figure panel — for the WHOLE funnel session, photos or
  // not, for EVERY lead (photo-gated-price-v1 hardwired 2026-08-25, so the gate
  // reduces to "is this a price surface at all"). Owner pivot 2026-07-28:
  // estimate-only, the official estimate is prepared off-funnel by a
  // coordinator and sent within 24h, ~1h once booked. Uploading photos only
  // switches the panel's copy from "add photos" to "on the way"; it never
  // reveals a price. The $500 booking FEE is not a procedure price and stays
  // visible throughout.
  const priceLocked = showPrices;
  const showBookingPanel =
    showPrices && showBookingCTA && selectedSurgeon && quoteId;

  // Secondary "Free evaluation" CTA (shipped via A/B free-evaluation-cta-v1,
  // resolved → treatment 2026-07-18): hidden once the inline payment panel is
  // up (bookingPaymentMode). Like Book Now it renders always and is disabled
  // until photos are uploaded. showBookingCTA scopes it to the photos/booking
  // context; it renders inside the showBookingPanel block.
  // With the photo gate hardwired (2026-08-25) Free evaluation is the ONLY
  // main-view CTA on every device (Book Now lives in its confirmation modal), so
  // the old mobile-only restriction is gone — desktop would otherwise have no
  // CTA at all.
  const showFreeEvalCta = showBookingCTA && !bookingPaymentMode;

  // Per-procedure display amounts from the shared pricing calculator:
  // row 0 bundles the OR-cost block + first surgeon-fee tier so the
  // patient sees a single "all-in" number; subsequent rows show just
  // the procedure price + the additional surgeon-fee tier.
  const patientDisplayRows = selectedSurgeon ? getPatientDisplayRows() : [];
  const displayRowById = new Map(
    patientDisplayRows.map((row) => [row.procedureId, row]),
  );
  const orderedProcedures: Procedure[] = selectedSurgeon
    ? patientDisplayRows
        .map((row) =>
          selectedProcedures.find((p: Procedure) => p.id === row.procedureId),
        )
        .filter((p): p is Procedure => !!p)
    : [];

  const quotePricing = selectedSurgeon ? getQuotePricing() : null;

  const handleRemoveProcedure = (procedure: Procedure) => {
    removeSelectedProcedure(procedure);
  };

  const handleRemoveProcedureType = (procedureTypeId: string) => {
    setSelectedProcedureTypes(
      selectedProcedureTypes.filter(
        (procedureType) => procedureType.id !== procedureTypeId,
      ),
    );
  };

  // --- Funnel price-display cap ------------------------------------------
  // Above this cap we stop quoting an exact number on the funnel and route the
  // patient to a coordinator for a bespoke quote. Per-tenant override via
  // appConfig.quoteDisplayCap; defaults to $15k. Applies to EVERY quoted
  // procedure figure shown in the summary (headline, per-procedure rows,
  // procedure-type ranges, financing totals) so no number over the cap is ever
  // shown — the booking deposit is a fixed reservation fee and is never capped.
  // Declared here (above the procedure rows that consume it) to stay out of the
  // temporal dead zone.
  const quoteDisplayCap =
    appConfig.data?.quoteDisplayCap ?? DEFAULT_QUOTE_DISPLAY_CAP;
  // Thin per-render closure over the shared cap formatter so the call sites stay
  // terse; clamps any quoted figure above the cap to "$15,000+" (full-cap). The
  // "as low as $X/mo" financing estimate intentionally still uses the real total.
  const formatCappedCurrency = (amount: number): string =>
    capCurrency(amount, quoteDisplayCap);

  // Calculate the total price
  // `totalPrice` is the modifier-free reference total; the "Starting
  // from" number patients see is `totalLowest` (procedure portion halved
  // for negotiation room). `adjustedTotalPrice` keeps the existing name
  // but now sources from totalLowest so the financing CTA and headline
  // stay in sync.
  const totalPrice = quotePricing?.totalLowest ?? quotePricing?.total ?? 0;
  const procedureDiscountDollars =
    appliedPromoCode?.target === "PROCEDURE_PRICE"
      ? appliedPromoCode.amountOffCents / 100
      : 0;
  const adjustedTotalPrice = Math.max(totalPrice - procedureDiscountDollars, 0);

  // Same figures the control hero shows — reused by the financing-forward
  // variant's demoted total so the ONLY difference between arms is
  // presentation, never the quoted price.
  const displayedTotal = Math.max(
    (quotePricing?.totalLowest ?? adjustedTotalPrice) - procedureDiscountDollars,
    0,
  );

  // True when the headline floor itself exceeds the cap. Over-cap always
  // renders the PriceCapPanel coordinator panel instead of a dollar figure —
  // see the quote-cap-no-price-v1 concluded-test note below.
  const isOverDisplayCap = displayedTotal > quoteDisplayCap;

  // The two reasons this surface swaps figures for the PriceCapPanel: the photo
  // gate's locked estimate (unconditional since 2026-08-25, so priceLocked
  // subsumes the other on any price surface) and the over-cap coordinator panel.
  // Kept as a single branch selector for every price-vs-panel site below, so a
  // new price site can never be gated on one reason and forget the other.
  const suppressPriceBlock = priceLocked || isOverDisplayCap;

  // Pure derivation over quotePricing/procedureDiscountDollars, both defined
  // above.
  const displayedUpToTotal =
    quotePricing && quotePricing.totalWithModifiers > quotePricing.totalLowest
      ? Math.max(quotePricing.totalWithModifiers - procedureDiscountDollars, 0)
      : null;

  // quote-cap-no-price-v1 A/B CONCLUDED 2026-07-28 — no_price won and now
  // serves 100% of over-cap traffic, so we no longer call useABTestVariant.
  // Over-cap unconditionally renders the PriceCapPanel coordinator panel (no
  // dollar figure); the old control-arm rendering — the capped "$15,000+"
  // headline/per-procedure/range figures and the green coordinator note beside
  // them — has been deleted. See abTestConfigs.ts retired block + the
  // ABTestResolution server overlay.

  const formatProcedureTypeRange = (
    minPrice: number | undefined,
    maxPrice: number | undefined,
  ) => {
    if (minPrice === undefined || maxPrice === undefined) {
      return t("summary.priceRangeUnavailable", {
        defaultValue: "Price range unavailable",
      });
    }

    // Range max above the cap while the headline total is still under it
    // shows "from $min" — a real price, never "$15,000+". (A total over the
    // cap hides the whole cell instead — see the isOverDisplayCap gates
    // below.)
    if (maxPrice > quoteDisplayCap) {
      // Whole range above the cap: show the translated fallback rather than a
      // real over-cap figure or a silent blank.
      if (minPrice > quoteDisplayCap) {
        return t("summary.priceRangeUnavailable", {
          defaultValue: "Price range unavailable",
        });
      }
      return t("summary.priceRangeFrom", {
        defaultValue: "from {{amount}}",
        amount: formatCurrency(minPrice),
      });
    }

    if (minPrice === maxPrice) {
      return formatCappedCurrency(minPrice);
    }

    return `${formatCappedCurrency(minPrice)} - ${formatCappedCurrency(maxPrice)}`;
  };

  const renderedProcedures =
    hasSimplifiedProcedureTypes
      ? selectedProcedureTypeRanges.map(({ procedureType, minPrice, maxPrice }) => {
          return (
            <tr key={procedureType.id}>
              <td valign="top">
                <CloseCircleOutlined
                  onClick={() => {
                    handleRemoveProcedureType(procedureType.id);
                  }}
                />{" "}
                {tProcedures(`types.${procedureType.name}`, {
                  defaultValue: procedureType.name,
                })}
              </td>
              <td valign="top">
                {/* Blanked while locked — the replacement copy is the
                    ranges-mode locked panel rendered after this list (the
                    surgeon-gated price block below never fires here). Over-cap
                    can't reach this cell: ranges mode has no surgeon, so there
                    is no total and isOverDisplayCap is structurally false —
                    over-cap ranges are handled per-cell inside
                    formatProcedureTypeRange instead. */}
                {showPrices && !suppressPriceBlock
                  ? formatProcedureTypeRange(minPrice, maxPrice)
                  : null}
              </td>
            </tr>
          );
        })
      : showPrices && orderedProcedures.length > 0
      ? orderedProcedures.map((procedure: Procedure) => {
          const row = displayRowById.get(procedure.id);
          return (
            <tr key={procedure.id}>
              <td valign="top">
                <CloseCircleOutlined
                  onClick={() => {
                    handleRemoveProcedure(procedure);
                  }}
                />{" "}
                {procedureDisplayName(procedure, {
                  language: i18n?.language,
                  t: tProcedures,
                })}
              </td>
              <td valign="top">
                {row && !suppressPriceBlock && row.amount <= quoteDisplayCap ? (
                  <>
                    {formatCappedCurrency(row.amount)}
                    {row.includesBaseCosts && (
                      <div style={{ fontSize: "0.85em", color: "#666" }}>
                        (includes facility &amp; surgeon fees)
                      </div>
                    )}
                    {row.amountWithModifiers > row.amount &&
                      row.amountWithModifiers <= quoteDisplayCap && (
                      <div style={{ fontSize: "0.85em", color: "#a16207" }}>
                        {t("summary.regularly", {
                          defaultValue: "Regularly {{amount}}",
                          amount: formatCappedCurrency(row.amountWithModifiers),
                        })}
                      </div>
                    )}
                  </>
                ) : null}
              </td>
            </tr>
          );
        })
      : selectedProcedures.map((procedure: Procedure) => {
          return (
            <tr key={procedure.id}>
              <td valign="top">
                <CloseCircleOutlined
                  onClick={() => {
                    handleRemoveProcedure(procedure);
                  }}
                />{" "}
                {procedureDisplayName(procedure, {
                  language: i18n?.language,
                  t: tProcedures,
                })}
              </td>
              <td valign="top"></td>
            </tr>
          );
        });

  // const renderedProcedures = selectedProcedures.map((procedure: Procedure) => {
  //   return (
  //     <tr key={procedure.id}>
  //       <td valign="top">
  //         <CloseCircleOutlined
  //           onClick={() => {
  //             handleRemoveProcedure(procedure);
  //           }}
  //         />{" "}
  //         {procedure.name}
  //       </td>
  //       <td valign="top"></td>
  //     </tr>
  //   );
  // });

  const bookingDiscountCents =
    appliedPromoCode?.target === "BOOKING_PRICE"
      ? appliedPromoCode.amountOffCents
      : 0;
  // Booking-fee anchor (2026-06-11 structure): every patient sees the $500
  // STANDARD fee on the page. The only $99 the funnel itself renders is a
  // Friends & Family Pass the patient possesses (giftPass). $249 and any
  // team-issued $99 are delivered ONLY via signed /action/book links carried
  // in email/SMS — they never auto-surface on the funnel. The legacy
  // followUpDiscount (discountStatus=ELIGIBLE auto-$99) lane was retired here:
  // a patient must never see $99 by asking/waiting — waiting gets $249 at most.
  // See docs/payments/14_BOOKING_LINK_FLOW.md + the coordinator structure doc.
  const bookingBasePriceCents = giftPass ? 9900 : 50000;
  // Note: when giftPass is active the promo-code input is still shown but the
  // server ignores the promo when a pass authorizes the $99 (pass wins).
  // A future nicety could suppress the promo input visually — deferred v1.

  // Pass price is flat $99 — a BOOKING_PRICE promo never stacks on it (the
  // server drops the promo when a pass authorizes the $99), so the display
  // must not subtract it either or the shown price won't match the charge.
  const bookingDisplayPriceCents = giftPass
    ? 9900
    : Math.max(bookingBasePriceCents - bookingDiscountCents, 50);

  // Monthly payment shown next to the financing CTA — driven directly by
  // the actual total now (no inflate/discount multiplier).
  const displayTotal = adjustedTotalPrice;
  const monthlyPayment = estimateMonthlyPayment(displayTotal);

  // The treatment leads with the monthly figure, so it renders only when there
  // is a real one — a $0 price (e.g. no priced procedures) falls back to the
  // control layout instead of an "As low as $0/mo" hero.
  const showFinancingTreatment = financingForward && monthlyPayment > 0;

  // Primary financing partner (config-driven, per tenant). Absent → the CTA
  // falls back to the read-only financing modal so it's never a dead button.
  const financingProvider = appConfig.data?.financingProviders?.[0] ?? null;
  const financingSurface: "mobile" | "desktop" = isMobile
    ? "mobile"
    : "desktop";

  const handleApplyFinancing = () => {
    // Reuse the promo CTA's ghost-tap guard so a buffered tap from the
    // previous step's "Next" can't fling a third-party tab open.
    if (!promoCodeReady) return;
    // No external apply URL configured for this tenant → nothing to do. The
    // read-only CareCredit modal that used to open here was DELETED 2026-09-13:
    // it was a wall of hardcoded dollar figures, it could never open anyway
    // (priceLocked === showPrices, and every call site sits inside a showPrices
    // branch, so the guard above always returned first), and resurrecting it
    // would break the estimate-only no-price rule.
    if (!financingProvider?.applyUrl) return;
    trackFinancingApplyClick({
      provider: financingProvider.provider,
      surface: financingSurface,
      monthlyPayment,
      total: displayedTotal,
    });
    // Persist the intent onto the quote so a coordinator sees the patient
    // went to the financing provider (NOT booked/approved).
    markFinancingApplyClicked(financingProvider.provider);
    window.open(
      withFinancingUtm(financingProvider.applyUrl, "quote_funnel"),
      "_blank",
      "noopener,noreferrer",
    );
  };

  // No-price mode replacement for the price block: divider + coordinator
  // panel, zero dollar figures. The prequalify link renders only when the
  // tenant has a real external apply URL — the read-only financing modal
  // fallback is all numbers and must never open from the number-free panel.
  // Two tones share this block: the over-cap coordinator panel and the
  // photo-gate treatment's estimate-only panel, whose pre-photo action is
  // uploading (photosIn drops that CTA — the ask is already answered). That
  // upload CTA drives the same hidden file input as the photo card above —
  // both price-surface call sites pass showFileUploads alongside showPricing,
  // so the input is always mounted wherever priceLocked can be true.
  const renderPriceCapPanelRows = (withFinancingLine: boolean) => (
    <>
      <tr>
        <td colSpan={2}>
          <Divider orientation="left">
            {priceLocked
              ? t("summary.photoGate.divider", {
                  defaultValue: "Your personalized estimate",
                })
              : t("summary.priceCap.divider", {
                  defaultValue: "Your personalized quote",
                })}
          </Divider>
        </td>
      </tr>
      <tr>
        <td colSpan={2}>
          <PriceCapPanel
            tone={priceLocked ? "photoGate" : "cap"}
            photosIn={hasUploadedPhotos}
            showFinancingLine={withFinancingLine}
            onApplyFinancing={
              // photoGate: no financing on the panel — the eval modal is the
              // one home of Book Now + financing apply (owner 2026-07-28).
              !priceLocked && financingProvider?.applyUrl
                ? handleApplyFinancing
                : undefined
            }
            applyDisabled={!promoCodeReady}
            applyLabel={financingProvider?.label}
            softPull={Boolean(
              !priceLocked &&
                financingProvider?.softPull &&
                financingProvider?.applyUrl,
            )}
            onUpload={
              priceLocked && !hasUploadedPhotos
                ? () => photoFileInputRef.current?.click()
                : undefined
            }
            onSkipWait={
              // Post-photos only: opens the eval modal (the treatment's one
              // home of Book Now + financing apply). Same intent signal as
              // the Free-evaluation button. showBookingPanel guard: the modal
              // mounts inside the booking panel, so without it (e.g. ranges
              // mode, no surgeon) the prompt would silently no-op.
              priceLocked && hasUploadedPhotos && showBookingPanel
                ? () => {
                    markEvaluationRequested();
                    setEvalModalOpen(true);
                  }
                : undefined
            }
          />
        </td>
      </tr>
    </>
  );

  // Whether an apply control actually renders on the summary right now —
  // mirrors the two real CTA render paths below (2026-09-13, see
  // trackFinancingForwardExposure's doc comment): the plain financing-hero
  // button (only reachable when !suppressPriceBlock, which already implies
  // !priceLocked, and only paints a CTA when showFinancingTreatment is true —
  // the !showFinancingTreatment branch is a bare "Total" row with no button),
  // or PriceCapPanel's onApplyFinancing, which the panel itself only wires up
  // when `!priceLocked && financingProvider?.applyUrl`. Under the hardwired
  // photo gate priceLocked is always true here, so this is always false today
  // — that IS the bug this field exists to make visible, not a new one.
  // The `!suppressPriceBlock` arm has to mirror BOTH financing-hero sites, not
  // just the in-table one: the in-table hero renders at
  // `showFinancingTreatment && !showBookingCTA`, and the below-panel booking
  // hero at `showBookingCTA && showFinancingTreatment && !bookingPaymentMode`.
  // Reporting `showFinancingTreatment` alone over-counted the booking surface
  // while a payment panel was up. Dead arm today either way (see above).
  const financingCtaPresentOnSummary = suppressPriceBlock
    ? Boolean(!priceLocked && financingProvider?.applyUrl)
    : showFinancingTreatment && (!showBookingCTA || !bookingPaymentMode);

  // Fire the financing-exposure diagnostic once PER QUOTE (not per mount). The
  // summary remounts across price-bearing steps, so a per-instance ref would
  // double-count; sessionStorage survives remounts within the tab. The ref is a
  // fallback when storage is blocked (private mode / quota). Server-backed via
  // quoteAnalytics. (Now single-arm — the financing-forward-v1 test concluded —
  // but the event still measures financing intent.)
  const financingExposureFired = useRef(false);
  useEffect(() => {
    if (!showPrices || !selectedSurgeon) return;
    const dedupeKey = quoteId ? `ff_exposure:financing-forward-v1:${quoteId}` : null;
    if (dedupeKey) {
      try {
        if (sessionStorage.getItem(dedupeKey)) return;
        sessionStorage.setItem(dedupeKey, "1");
      } catch {
        if (financingExposureFired.current) return;
        financingExposureFired.current = true;
      }
    } else {
      if (financingExposureFired.current) return;
      financingExposureFired.current = true;
    }
    trackFinancingForwardExposure({
      surface: financingSurface,
      bookingSurface: showBookingCTA,
      monthlyPayment,
      total: displayedTotal,
      ctaPresent: financingCtaPresentOnSummary,
      context: "summary",
    });
  }, [
    showPrices,
    selectedSurgeon,
    quoteId,
    financingSurface,
    showBookingCTA,
    monthlyPayment,
    displayedTotal,
    financingCtaPresentOnSummary,
  ]);

  // Second exposure fire: when the eval modal opens with a real financing
  // apply button rendered (context: "eval_modal" — see the modal JSX below,
  // gated on financingProvider?.applyUrl). This is the CTA that actually
  // survived PR #1816's photo-gate hardwiring (owner decision 2026-07-28: the
  // eval modal is the one home of Book Now + financing apply), so it always
  // fires with ctaPresent: true. Deduped separately from the summary's
  // ff_exposure key — a session can log a (currently CTA-less) summary
  // exposure and a genuine eval-modal one for the same quote.
  const evalModalExposureFired = useRef(false);
  useEffect(() => {
    if (!evalModalOpen || !financingProvider?.applyUrl) return;
    const dedupeKey = quoteId
      ? `ff_exposure:financing-forward-v1:eval-modal:${quoteId}`
      : null;
    if (dedupeKey) {
      try {
        if (sessionStorage.getItem(dedupeKey)) return;
        sessionStorage.setItem(dedupeKey, "1");
      } catch {
        if (evalModalExposureFired.current) return;
        evalModalExposureFired.current = true;
      }
    } else {
      if (evalModalExposureFired.current) return;
      evalModalExposureFired.current = true;
    }
    trackFinancingForwardExposure({
      surface: financingSurface,
      bookingSurface: showBookingCTA,
      monthlyPayment,
      total: displayedTotal,
      ctaPresent: true,
      context: "eval_modal",
    });
  }, [
    evalModalOpen,
    financingProvider,
    quoteId,
    financingSurface,
    showBookingCTA,
    monthlyPayment,
    displayedTotal,
  ]);

  const handleApplyPromoCode = async () => {
    if (!promoCodeInput.trim()) {
      setPromoCodeError(t("summary.promoEnterCode"));
      setAppliedPromoCode(null);
      return;
    }

    setIsApplyingPromoCode(true);
    setPromoCodeError("");

    try {
      const response = await fetch(
        `${process.env.PUBLIC_URL}/api/validate-promo-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ code: promoCodeInput.trim() }),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.valid || !data.promoCode) {
        setAppliedPromoCode(null);
        setPromoCodeError(t("summary.promoInvalid"));
        return;
      }

      setAppliedPromoCode(data.promoCode as PromoCodePayload);
      setPromoCodeError("");
      setPromoCodeInput(data.promoCode.code);
    } catch (error) {
      console.error("Failed to validate promo code", error);
      setAppliedPromoCode(null);
      setPromoCodeError(t("summary.promoUnavailable"));
    } finally {
      setIsApplyingPromoCode(false);
    }
  };


  return (
    <div className="quote-summary">
      {/* <div className="quote-summary-title">
        {name !== "" ? `${name}'s Summary` : "Summary"}
      </div> */}
      <div className="quote-summary-content">
        {showFileUploads && !bookingPaymentMode && (
          <>
            <style>{`
              @keyframes ta-photo-pulse {
                0%, 100% { box-shadow: 0 6px 20px rgba(245, 158, 11, 0.35); }
                50%      { box-shadow: 0 8px 28px rgba(245, 158, 11, 0.55); }
              }
            `}</style>
            <input
              ref={photoFileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                // input.files is a LIVE FileList — resetting value below
                // empties it, so the async upload loop would only ever see
                // the first file. Snapshot into a real array first.
                const files = Array.from(e.target.files ?? []);
                e.target.value = "";
                if (files.length > 0) handlePhotoUpload(files);
              }}
            />
            {/* Photo upload — promotion card morphs between two states */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => {
                if (!isUploadingPhotos) {
                  photoFileInputRef.current?.click();
                }
              }}
              style={{
                position: "relative",
                margin: "8px 7px 12px 7px",
                padding: hasUploadedPhotos ? "14px 16px" : "18px 18px 16px",
                borderRadius: "14px",
                cursor: "pointer",
                color: "#fff",
                background: hasUploadedPhotos
                  ? "linear-gradient(135deg, #059669 0%, #10b981 100%)"
                  : "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
                boxShadow: hasUploadedPhotos
                  ? "0 4px 12px rgba(16, 185, 129, 0.25)"
                  : "0 6px 20px rgba(245, 158, 11, 0.35)",
                animation: hasUploadedPhotos
                  ? undefined
                  : "ta-photo-pulse 2.2s ease-in-out infinite",
                transition: "all 0.3s ease",
              }}
            >
              {!hasUploadedPhotos && (
                <div
                  style={{
                    position: "absolute",
                    top: "-10px",
                    right: "12px",
                    background: "#fff",
                    color: "#ef4444",
                    fontWeight: 800,
                    fontSize: "13px",
                    padding: "4px 10px",
                    borderRadius: "999px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    letterSpacing: "0.3px",
                  }}
                >
                  {t("photos.pillIn24", { defaultValue: "IN 24hs" })}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: "28px",
                    flexShrink: 0,
                    lineHeight: 1,
                  }}
                >
                  {uploadProgress ? (
                    <LoadingOutlined spin />
                  ) : hasUploadedPhotos ? (
                    <CheckCircleOutlined />
                  ) : (
                    <PictureOutlined />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {uploadProgress ? (
                    // In-flight batch state takes precedence over the
                    // hasUploadedPhotos success branch below — otherwise the
                    // card flips to "Photos received!" the moment photo 1 of
                    // N lands, while N-1 are still uploading (owner report
                    // 2026-08-14).
                    <>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 800,
                          lineHeight: 1.2,
                          letterSpacing: "0.2px",
                        }}
                      >
                        {uploadProgress.total > 1
                          ? t("quoteSummary.uploadingProgress", {
                              current: Math.min(
                                uploadProgress.done + 1,
                                uploadProgress.total,
                              ),
                              total: uploadProgress.total,
                            })
                          : t("quoteSummary.uploading")}
                      </div>
                      {fileList.length > 0 && (
                        <div
                          style={{
                            fontSize: "12px",
                            opacity: 0.92,
                            marginTop: "3px",
                          }}
                        >
                          {fileList.length === 1
                            ? t("quoteSummary.photosUploaded", {
                                count: fileList.length,
                              })
                            : t("quoteSummary.photosUploadedPlural", {
                                count: fileList.length,
                              })}
                        </div>
                      )}
                    </>
                  ) : hasUploadedPhotos ? (
                    <>
                      <div
                        style={{
                          fontSize: "15px",
                          fontWeight: 700,
                          lineHeight: 1.2,
                        }}
                      >
                        {t("photos.estimateUnlocked", {
                          defaultValue:
                            "Photos received! Your official estimate is on the way.",
                        })}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          opacity: 0.92,
                          marginTop: "3px",
                        }}
                      >
                        {fileList.length === 1
                          ? t("quoteSummary.photosUploaded", {
                              count: fileList.length,
                            })
                          : t("quoteSummary.photosUploadedPlural", {
                              count: fileList.length,
                            })}
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 800,
                          lineHeight: 1.2,
                          letterSpacing: "0.2px",
                        }}
                      >
                        {/* No isUploadingPhotos check here anymore: any
                            in-flight upload renders the uploadProgress
                            branch above, so this prompt state can only
                            show while idle. */}
                        {t("photos.unlockSurgeonEstimate", {
                          defaultValue:
                            "Add your photos to unlock your surgeon reviewed estimate",
                        })}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          opacity: 0.95,
                          marginTop: "4px",
                          fontWeight: 600,
                        }}
                      >
                        {t("quoteSummary.uploadPhotos")} →
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* The specials disclaimer is gone with the control arm — no
                discount is MESSAGED anywhere in the funnel any more (owner
                2026-07-28: discount framing doesn't fit estimate-only;
                hardwired for everyone 2026-08-25). The discount itself still
                applies in the coordinator's calculation. */}
          </>
        )}

        {!bookingPaymentMode && (
        <table
          style={{ textAlign: "left", color: "black", width: "100%" }}
          cellPadding={7}
        >
          <tbody>
            {selectedSurgeon && (
              <>
                <tr>
                  <td colSpan={2}>
                    <Divider orientation="left">
                      {t("summary.yourSurgeon")}
                    </Divider>
                  </td>
                </tr>
                <tr>
                  <td>
                    <img
                      style={{
                        height: "60px",
                        width: "60px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                      src={`${process.env.PUBLIC_URL}/uploaded_images/${selectedSurgeon?.image}`}
                    />
                  </td>
                  <td>
                    <div>{selectedSurgeon?.name}</div>
                    {selectedSurgeon?.nickname && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginTop: "2px",
                        }}
                      >
                        {selectedSurgeon.nickname}
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td colSpan={2}>
                    <TrustBand />
                  </td>
                </tr>
              </>
            )}
            <tr>
              <td colSpan={2}>
                <Divider orientation="left">{t("summary.procedures")}</Divider>
              </td>
            </tr>

            {renderedProcedures}

            {/* Ranges mode (procedure TYPES selected) clears the surgeon —
                setSelectedProcedureTypes structurally clears it — but
                QuoteApp's single-surgeon auto-reselect re-arms selectedSurgeon
                even in ranges mode, so !selectedSurgeon here only holds in
                multi-surgeon configs; the surgeon-gated panel sites below
                cover the single-surgeon case instead. This is the ONE
                locked-panel site that doesn't require a surgeon —
                !selectedSurgeon makes it mutually exclusive with all three
                surgeon-gated panel sites. priceLocked is unconditional on a
                price surface now (photo gate hardwired 2026-08-25), so this
                panel renders for every ranges-mode visitor — without this
                branch they would get the range cells blanked above and nothing
                in their place. No financing line: with no surgeon there's no
                quotePricing to finance. */}
            {/* LATENT (2026-09-13 audit, no behavior change): in ranges mode
                this panel is rendered with showFinancingLine=false and, because
                !selectedSurgeon also switches off showBookingPanel, the surface
                ends up with NO call to action at all. Unreachable on the single
                -surgeon tenant we run today (QuoteApp auto-reselects the only
                surgeon, so !selectedSurgeon never holds); it becomes real the
                first time a tenant has more than one surgeon, and the fix then
                is to give this branch its own CTA rather than to loosen the
                guard. */}
            {!selectedSurgeon &&
              hasSimplifiedProcedureTypes &&
              priceLocked &&
              renderPriceCapPanelRows(false)}

            {selectedSurgeon && showPrices && (
              <>
                {showFinancingTreatment && !showBookingCTA ? (
                  suppressPriceBlock ? (
                    renderPriceCapPanelRows(true)
                  ) : (
                  <>
                    <tr>
                      <td colSpan={2}>
                        <Divider orientation="left">
                          {t("summary.financingHero.divider", {
                            defaultValue: "Pay monthly",
                          })}
                        </Divider>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <div className="financing-hero financing-hero--split">
                          <div className="financing-hero__offer">
                            <div className="financing-hero__eyebrow">
                              {t("summary.financingHero.eyebrow", {
                                defaultValue: "As low as",
                              })}
                            </div>
                            <div className="financing-hero__amount">
                              ${Number(monthlyPayment).toFixed(0)}
                              <span className="financing-hero__amount-suffix">
                                /mo
                              </span>
                            </div>
                            <div className="financing-hero__qualifier">
                              {t("summary.financingHero.qualifier", {
                                defaultValue:
                                  "est. over 60 mo · subject to approval",
                              })}
                            </div>
                            <button
                              type="button"
                              className="financing-hero__cta"
                              disabled={!promoCodeReady}
                              onClick={handleApplyFinancing}
                            >
                              {(financingProvider?.label ||
                                t("summary.financingHero.cta", {
                                  defaultValue: "See if you prequalify",
                                })) + " →"}
                            </button>
                            {financingProvider?.softPull &&
                              financingProvider?.applyUrl && (
                              <div className="financing-hero__softpull">
                                🛡{" "}
                                {t("summary.financingHero.softPull", {
                                  defaultValue:
                                    "Soft check — won't affect your credit score",
                                })}
                              </div>
                            )}
                          </div>
                          <div className="financing-hero__total">
                            <div className="financing-hero__total-label">
                              {t("summary.financingHero.totalLabel", {
                                defaultValue: "One-time total",
                              })}
                            </div>
                            <div className="financing-hero__total-amount">
                              {formatCappedCurrency(displayedTotal)}
                            </div>
                            {displayedUpToTotal !== null &&
                              displayedUpToTotal <= quoteDisplayCap && (
                              <div className="financing-hero__total-upto">
                                {t("summary.regularly", {
                                  defaultValue: "Regularly {{amount}}",
                                  amount: formatCappedCurrency(displayedUpToTotal),
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </>
                  )
                ) : !showFinancingTreatment ? (
                  suppressPriceBlock ? (
                    renderPriceCapPanelRows(false)
                  ) : (
                  <>
                    <tr>
                      <td colSpan={2}>
                        <Divider orientation="left">Total</Divider>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: "bold", textAlign: "right" }}>
                        {t("summary.todaysPrice")}
                      </td>
                      <td>
                        {quotePricing &&
                          quotePricing.totalWithModifiers >
                            quotePricing.totalLowest &&
                          displayedUpToTotal !== null &&
                          displayedUpToTotal <= quoteDisplayCap && (
                            <div style={{ marginBottom: "2px" }}>
                              <span
                                style={{
                                  fontSize: "0.9em",
                                  color: "#9ca3af",
                                  textDecoration: "line-through",
                                }}
                              >
                                {formatCappedCurrency(
                                  Math.max(
                                    quotePricing.totalWithModifiers -
                                      procedureDiscountDollars,
                                    0,
                                  ),
                                )}
                              </span>{" "}
                              <span
                                style={{ fontSize: "0.8em", color: "#9ca3af" }}
                              >
                                {t("summary.regularPrice")}
                              </span>
                            </div>
                          )}
                        <div
                          style={{
                            fontSize: "1.25em",
                            fontWeight: 700,
                            color: "#15803d",
                          }}
                        >
                          {formatCappedCurrency(displayedTotal)}
                        </div>
                        {quotePricing &&
                          quotePricing.totalWithModifiers >
                            quotePricing.totalLowest && (
                            <div
                              style={{
                                fontSize: "0.78em",
                                color: "#16a34a",
                                fontWeight: 600,
                                marginTop: "2px",
                              }}
                            >
                              {t("summary.onlineDiscountApplied")}
                            </div>
                          )}
                      </td>
                    </tr>
                  </>
                  )
                ) : null}
                {/* An unbounded promo (amountOffCents has no max) can exceed
                    the cap while clamping displayedTotal under it — cap-gate
                    the discount figure too so no over-cap number leaks
                    through the promo row. */}
                {appliedPromoCode?.target === "PROCEDURE_PRICE" &&
                  !suppressPriceBlock &&
                  procedureDiscountDollars <= quoteDisplayCap && (
                  <tr>
                    <td style={{ fontWeight: "bold", textAlign: "right" }}>
                      Promo ({appliedPromoCode.code})
                    </td>
                    <td>-{formatCurrency(procedureDiscountDollars)}</td>
                  </tr>
                )}
                {!isEnabled("enablePromoCodes") ? null : !showPromoCodeInput ? (
                  <tr>
                    <td colSpan={2}>
                      <Button
                        type="link"
                        onClick={handleShowPromoCode}
                        disabled={!promoCodeReady}
                        style={{ padding: 0, textAlign: "left" }}
                      >
                        {t("summary.doYouHavePromo")}
                      </Button>
                    </td>
                  </tr>
                ) : (
                  <tr>
                        <td style={{ fontWeight: "bold", textAlign: "right" }}>
                          {t("summary.promoCode")}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                            }}
                          >
                            <Input
                              value={promoCodeInput}
                              placeholder={t("summary.enterPromoCode")}
                              onChange={(e) =>
                                setPromoCodeInput(e.target.value)
                              }
                              style={{ maxWidth: "180px" }}
                              autoFocus
                            />
                            <Button
                              type="default"
                              size="small"
                              loading={isApplyingPromoCode}
                              onClick={handleApplyPromoCode}
                            >
                              {t("summary.apply")}
                            </Button>
                          </div>
                          {promoCodeError && (
                            <div
                              style={{
                                color: "#b91c1c",
                                fontSize: "12px",
                                marginTop: "4px",
                              }}
                            >
                              {promoCodeError}
                            </div>
                          )}
                          {appliedPromoCode && !promoCodeError && (
                            <div
                              style={{
                                color: "#166534",
                                fontSize: "12px",
                                marginTop: "4px",
                              }}
                            >
                              {t("summary.appliedDiscount", {
                                code: appliedPromoCode.code,
                                type:
                                  appliedPromoCode.target === "BOOKING_PRICE"
                                    ? t("summary.bookingType")
                                    : t("summary.procedureType"),
                              })}
                          </div>
                        )}
                      </td>
                    </tr>
                )}
                {/* financing-forward-v1 concluded (treatment won): the old
                    control-arm "Financing available $X/mo" card was removed.
                    With one arm, monthlyPayment>0 always renders the hero above;
                    the only state this card could still hit is monthlyPayment<=0
                    (a $0 quote), where "$0/mo financing" is nonsense. */}
                {showFinancingTreatment &&
                  !showBookingCTA &&
                  !suppressPriceBlock && (
                  <tr>
                    <td colSpan={2}>
                      <div className="financing-hero__disclosure">
                        {t("summary.financingHero.disclosure", {
                          defaultValue:
                            "Representative example only — not a loan offer or a guarantee of approval. Rates and terms vary by lender and credit approval. Financing provided by third-party lenders.",
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
        )}

        {showBookingPanel && (
          <div className="book-now-summary-panel" ref={bookingPanelRef}>
            {!isBooked ? (
              <>
                {bookingPaymentMode ? (
                  // Deposit-focused header — replaces the procedures/price
                  // context while the card form is up so it's clear they're
                  // paying a deposit to reserve a date, not the full procedure.
                  <div className="booking-focus-header">
                    <div className="booking-focus-title">
                      {t("booking.focusTitle", {
                        defaultValue: "Reserve your surgery date",
                      })}
                    </div>
                    <div className="booking-focus-amount">
                      {formatCurrency(
                        (bookingDepositCents ?? bookingDisplayPriceCents) / 100,
                      )}
                    </div>
                    <div className="booking-focus-label">
                      {t("booking.focusDepositLabel", {
                        defaultValue: "deposit to secure your date",
                      })}
                    </div>
                    <div className="booking-focus-note">
                      {t("booking.focusNote", {
                        defaultValue:
                          "This holds your surgery date — it's a deposit, not your full procedure cost. Fully refundable for 48 hours.",
                      })}
                    </div>
                  </div>
                ) : null}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: showFreeEvalCta ? 10 : 0,
                  }}
                >
                  {/* SINGLE BookNowButton instance — never branch it, or it
                      remounts when bookingPaymentMode flips and the live payment
                      panel (its internal state) is destroyed. Only the sibling
                      below toggles. Stacked full-width; the Free-evaluation
                      button mirrors Book Now's styling (no ghost variant).
                      Under the (now unconditional) photo gate it is visually
                      HIDDEN — never unmounted, since the eval modal's Book-now
                      fires it via bookNowRef and its inline payment panel lives
                      inside — until booking is triggered and bookingPaymentMode
                      shows the payment panel. */}
                  <div
                    style={!bookingPaymentMode ? { display: "none" } : undefined}
                  >
                    <BookNowButton
                      ref={bookNowRef}
                      quoteId={quoteId}
                      disabled={!hasUploadedPhotos}
                      priceCents={bookingDisplayPriceCents}
                      onPaymentModeChange={(active, cents) => {
                        setBookingPaymentMode(active);
                        if (active && typeof cents === "number") {
                          setBookingDepositCents(cents);
                        }
                      }}
                    />
                  </div>
                  {showFreeEvalCta && (
                    // Same button styling + size as Book Now (type/size +
                    // book-now-button class). Visible always; disabled until
                    // photos are uploaded, mirroring Book Now.
                    <Button
                      type="primary"
                      size="large"
                      disabled={!hasUploadedPhotos}
                      className="book-now-button"
                      onClick={() => {
                        markEvaluationRequested();
                        setEvalModalOpen(true);
                      }}
                    >
                      {t("photos.evalButton")}
                    </Button>
                  )}
                </div>
                {!hasUploadedPhotos && (
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#9ca3af", textAlign: "center" }}>
                    {t("booking.photosRequiredToBook", { defaultValue: "Photos required to book" })}
                  </div>
                )}
                {/* Confirmation modal for the "Free evaluation" CTA. "Book now"
                    closes the modal first, then fires booking in afterClose so
                    the modal fully unmounts before the inline payment panel
                    opens (no modal-over-panel stacking). Mirrors the welcome
                    modal conventions in StepPhotos.
                    Under the (now unconditional) photo gate this modal is the
                    ONLY home of Book Now and financing apply (both framed as
                    skipping the estimate line) — the main view carries just the
                    Free evaluation button. */}
                <Modal
                  open={evalModalOpen}
                  onCancel={() => setEvalModalOpen(false)}
                  afterClose={() => {
                    if (triggerBookingOnCloseRef.current) {
                      triggerBookingOnCloseRef.current = false;
                      bookNowRef.current?.trigger();
                    }
                  }}
                  footer={null}
                  centered
                  maskClosable={false}
                  width={isMobile ? "92%" : 480}
                >
                  <div style={{ padding: "12px 4px 0", textAlign: "center" }}>
                    <h2
                      style={{
                        fontSize: "22px",
                        fontWeight: 700,
                        color: "#111827",
                        lineHeight: 1.25,
                        marginTop: 0,
                        marginBottom: "12px",
                      }}
                    >
                      {t("photos.evalModal.titleGated")}
                    </h2>
                    <p
                      style={{
                        fontSize: "15px",
                        color: "#374151",
                        lineHeight: 1.55,
                        marginBottom: "10px",
                      }}
                    >
                      {t("photos.evalModal.bodyGated")}
                    </p>
                    {/* The skip-the-line pitch gets its own bold line (owner
                        2026-07-28). */}
                    <p
                      style={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: "#111827",
                        lineHeight: 1.55,
                        marginBottom: "20px",
                      }}
                    >
                      {t("photos.evalModal.bodyGatedSkip", {
                        defaultValue:
                          "Book now or get pre-approved for financing to skip the line: estimate in as little as 1 hour.",
                      })}
                    </p>
                    <QuoteButton
                      onClick={() => {
                        triggerBookingOnCloseRef.current = true;
                        setEvalModalOpen(false);
                      }}
                    >
                      {t("photos.evalModal.bookNowCta")}
                    </QuoteButton>
                    {/* Financing apply as the second skip-the-line path
                        (external Cherry prequal — no figure involved;
                        handleApplyFinancing never opens the read-only modal
                        when a real applyUrl exists). */}
                    {financingProvider?.applyUrl && (
                      <button
                        type="button"
                        className="financing-hero__cta"
                        style={{ marginTop: "10px" }}
                        disabled={!promoCodeReady}
                        onClick={() => {
                          setEvalModalOpen(false);
                          handleApplyFinancing();
                        }}
                      >
                        {(financingProvider?.label ||
                          t("summary.financingHero.cta", {
                            defaultValue: "See if you prequalify",
                          })) + " →"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setEvalModalOpen(false)}
                      style={{
                        display: "block",
                        margin: "14px auto 4px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "14px",
                        color: "#6b7280",
                        padding: "4px 8px",
                      }}
                    >
                      {t("photos.evalModal.dismissGated")}
                    </button>
                  </div>
                </Modal>
              </>
            ) : (
              <div className="book-now-summary-booked">
                <strong>{t("booking.booked")}</strong>
                <div>
                  {hasUploadedPhotos
                    ? t("booking.photosReceived")
                    : t("booking.addPhotosToLock")}
                </div>
              </div>
            )}
          </div>
        )}

        {showBookingCTA && showFinancingTreatment && !bookingPaymentMode && selectedSurgeon && showPrices && (
          <div style={{ margin: "0 7px 12px" }}>
            {suppressPriceBlock ? (
              // Mirror of the table's price-cap panel rows — this block
              // renders outside the <table> (below the booking panel) so it
              // needs its own divider + panel, not <tr>/<td>. Same two tones.
              <>
                <Divider orientation="left">
                  {priceLocked
                    ? t("summary.photoGate.divider", {
                        defaultValue: "Your personalized estimate",
                      })
                    : t("summary.priceCap.divider", {
                        defaultValue: "Your personalized quote",
                      })}
                </Divider>
                <PriceCapPanel
                  tone={priceLocked ? "photoGate" : "cap"}
                  photosIn={hasUploadedPhotos}
                  showFinancingLine
                  onApplyFinancing={
                    // photoGate: no financing on the panel — the eval modal is
                    // the one home of Book Now + financing apply.
                    !priceLocked && financingProvider?.applyUrl
                      ? handleApplyFinancing
                      : undefined
                  }
                  applyDisabled={!promoCodeReady}
                  applyLabel={financingProvider?.label}
                  softPull={Boolean(
                    !priceLocked &&
                      financingProvider?.softPull &&
                      financingProvider?.applyUrl,
                  )}
                  onUpload={
                    priceLocked && !hasUploadedPhotos
                      ? () => photoFileInputRef.current?.click()
                      : undefined
                  }
                  onSkipWait={
                    priceLocked && hasUploadedPhotos && showBookingPanel
                      ? () => {
                          markEvaluationRequested();
                          setEvalModalOpen(true);
                        }
                      : undefined
                  }
                />
              </>
            ) : (
              <>
                <Divider orientation="left">
                  {t("summary.financingHero.divider", { defaultValue: "Pay monthly" })}
                </Divider>
                <div className="financing-hero financing-hero--split">
                  <div className="financing-hero__offer">
                    <div className="financing-hero__eyebrow">
                      {t("summary.financingHero.eyebrow", { defaultValue: "As low as" })}
                    </div>
                    <div className="financing-hero__amount">
                      ${Number(monthlyPayment).toFixed(0)}
                      <span className="financing-hero__amount-suffix">/mo</span>
                    </div>
                    <div className="financing-hero__qualifier">
                      {t("summary.financingHero.qualifier", {
                        defaultValue: "est. over 60 mo · subject to approval",
                      })}
                    </div>
                    <button
                      type="button"
                      className="financing-hero__cta"
                      disabled={!promoCodeReady}
                      onClick={handleApplyFinancing}
                    >
                      {(financingProvider?.label ||
                        t("summary.financingHero.cta", {
                          defaultValue: "See if you prequalify",
                        })) + " →"}
                    </button>
                    {financingProvider?.softPull && financingProvider?.applyUrl && (
                      <div className="financing-hero__softpull">
                        🛡{" "}
                        {t("summary.financingHero.softPull", {
                          defaultValue: "Soft check — won't affect your credit score",
                        })}
                      </div>
                    )}
                  </div>
                  <div className="financing-hero__total">
                    <div className="financing-hero__total-label">
                      {t("summary.financingHero.totalLabel", { defaultValue: "One-time total" })}
                    </div>
                    <div className="financing-hero__total-amount">
                      {formatCappedCurrency(displayedTotal)}
                    </div>
                    {displayedUpToTotal !== null &&
                      displayedUpToTotal <= quoteDisplayCap && (
                      <div className="financing-hero__total-upto">
                        {t("summary.regularly", {
                          defaultValue: "Regularly {{amount}}",
                          amount: formatCappedCurrency(displayedUpToTotal),
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <div className="financing-hero__disclosure" style={{ marginTop: "8px" }}>
                  {t("summary.financingHero.disclosure", {
                    defaultValue:
                      "Representative example only — not a loan offer or a guarantee of approval. Rates and terms vary by lender and credit approval. Financing provided by third-party lenders.",
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* financing-forward-v1 concluded (treatment won): the old control-arm
            booking-step "Financing Options $X/month" row was removed — the
            treatment booking hero above covers monthlyPayment>0; this row could
            now only render at monthlyPayment<=0 ("$0/month"). */}
      </div>
    </div>
  );
};

export default QuoteSummaryTreatment;
